import React, { useState, useCallback } from 'react';
import { SellerLayout } from '../SellerLayout';
import {
  PageHeading,
  CardBalance,
  Pills,
  Button,
  Text,
  Tooltip,
  Drawer,
  Input,
  Alert,
  Loading,
  Icon,
  Avatar,
} from 'barte-design-system';
import {
  InfoIcon,
  ArrowRightIcon,
  CheckIcon,
} from '@phosphor-icons/react';
import './DashboardSaldos.css';

// ─── Tooltip cast (children omitido no tipo público) ─────────────────────────

const TooltipAny = Tooltip as React.ForwardRefExoticComponent<any>;

// ─── Info icons com tooltip (definidos fora do render para identidade estável) ─

const Card1InfoIcon: React.ElementType = () => (
  <TooltipAny
    title="Saldo total das vendas"
    label="Valor total das vendas, composto pelo saldo disponível e pelo saldo futuro, incluindo valores retidos em garantia operacional e comprometidos por gravames."
    placement="top"
  >
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Icon icon={InfoIcon} size={16} />
    </span>
  </TooltipAny>
);

const Card2Line1InfoIcon: React.ElementType = () => (
  <TooltipAny
    title="Saldo disponível"
    label="Valor que você pode solicitar saques. Parte dele pode ficar guardada como bolsão de garantia, para manter a segurança das operações."
    placement="top"
  >
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Icon icon={InfoIcon} size={16} />
    </span>
  </TooltipAny>
);

const Card2Line2InfoIcon: React.ElementType = () => (
  <TooltipAny
    title="Saldo futuro"
    label="Valor total a receber. Parte desse valor pode já estar comprometida por gravames, que são valores reservados para obrigações já assumidas."
    placement="top"
  >
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Icon icon={InfoIcon} size={16} />
    </span>
  </TooltipAny>
);

// ─── Types ────────────────────────────────────────────────────────────────────

type SaqueStep = 'empty' | 'filled' | 'processing' | 'confirmation';
type PeriodKey = 'week' | 'month' | 'jan';

// ─── Mock data ────────────────────────────────────────────────────────────────

const CHART_DATA = [
  { label: '01', received: 0, overdue: 0 },
  { label: '03', received: 0, overdue: 0 },
  { label: '05', received: 0, overdue: 0 },
  { label: '07', received: 0, overdue: 0 },
  { label: '08', received: 45, overdue: 40, isToday: true },
  { label: '11', received: 0, overdue: 0 },
  { label: '13', received: 0, overdue: 0 },
  { label: '15', received: 0, overdue: 0 },
  { label: '17', received: 0, overdue: 0 },
  { label: '18', received: 0, overdue: 0 },
  { label: '21', received: 0, overdue: 0 },
  { label: '23', received: 0, overdue: 0 },
  { label: '25', received: 0, overdue: 0 },
  { label: '27', received: 0, overdue: 0 },
  { label: '28', received: 0, overdue: 0 },
  { label: '31', received: 0, overdue: 0 },
];

const MAX_CHART_VALUE = Math.max(...CHART_DATA.map((d) => d.received + d.overdue), 1);

const RECEBIVEIS_MOCK = [
  {
    id: '1',
    initials: 'VG',
    name: 'Vinicius Gonçalves...',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 1,00',
    status: 'Em atraso' as const,
    statusState: 'error' as const,
  },
  {
    id: '2',
    initials: 'VG',
    name: 'Vinicius Gonçalves...',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 1,00',
    status: 'Paga - 06/01' as const,
    statusState: 'success' as const,
  },
];


// ─── Component ────────────────────────────────────────────────────────────────

export function DashboardSaldos() {

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saqueStep, setSaqueStep] = useState<SaqueStep>('empty');
  const [saqueValue, setSaqueValue] = useState('');
  const [valorRepasse, setValorRepasse] = useState(0);
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('month');

  // ── Drawer handlers ──────────────────────────────────────────────────────

  const handleOpenDrawer = useCallback(() => {
    setSaqueStep('empty');
    setSaqueValue('');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => {
      setSaqueStep('empty');
      setSaqueValue('');
    }, 300);
  }, []);

  const DISPONIVEL = 340000;
  const DISPONIVEL_TOTAL = 460000;

  const formatCurrency = (raw: string) => {
    const digits = raw.replaceAll(/\D/g, '');
    if (!digits) return '';
    const num = Number.parseInt(digits, 10) / 100;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseValue = (formatted: string) => {
    if (!formatted) return 0;
    return Number.parseFloat(formatted.replaceAll('.', '').replace(',', '.')) || 0;
  };

  const calcSaque = (formatted: string) => {
    const val = parseValue(formatted);
    const excede = val > DISPONIVEL_TOTAL;
    const taxa = val > DISPONIVEL ? (val - DISPONIVEL) * 0.05 : 0;
    const recebe = val - taxa;
    return { val, excede, taxa, recebe };
  };

  const handleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const formatted = formatCurrency(e.target.value);
      setSaqueValue(formatted);
      setSaqueStep(formatted ? 'filled' : 'empty');
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    const val = parseValue(saqueValue);
    const taxa = val > DISPONIVEL ? (val - DISPONIVEL) * 0.05 : 0;
    setValorRepasse(val - taxa);
    setSaqueStep('processing');
    setTimeout(() => setSaqueStep('confirmation'), 2000);
  }, [saqueValue]);

  // ── Drawer footer ─────────────────────────────────────────────────────────

  function getDrawerFooter() {
    if (saqueStep === 'confirmation') {
      return {
        primaryButton: { children: 'Entendi', onClick: handleCloseDrawer },
        secondaryButton: { children: 'Extrato de saques', onClick: handleCloseDrawer },
      };
    }

    return {
      primaryButton: {
        children: 'Solicitar saque',
        iconRight: <Icon icon={ArrowRightIcon} size={16} />,
        onClick: handleConfirm,
        disabled: saqueStep === 'empty' || saqueStep === 'processing' || calcSaque(saqueValue).excede,
      },
      secondaryButton: {
        children: 'Cancelar',
        onClick: handleCloseDrawer,
        disabled: saqueStep === 'processing',
      },
    };
  }

  // ── Drawer content ────────────────────────────────────────────────────────

  function renderDrawerContent() {
    if (saqueStep === 'processing') {
      return (
        <div className="drawer-loading">
          <Loading type="page" description="Carregando solicitação" />
        </div>
      );
    }

    if (saqueStep === 'confirmation') {
      return (
        <div className="drawer-confirmation">
          <div className="drawer-success-icon">
            <Icon icon={CheckIcon} size={32} color="white" weight="bold" />
          </div>
          <div className="drawer-confirmation-header">
            <Text variant="h4" style={{ textAlign: 'center' }}>Solicitação efetuada com sucesso!</Text>
            <Text variant="body-sm" style={{ textAlign: 'center', color: 'var(--content-secondary)' }}>
              O valor será descontado do saldo disponível e aparecerá como em processamento até ser creditado na conta corrente, em até 24h.
            </Text>
          </div>
          <div className="drawer-confirmation-section">
            <Text variant="label-lg">Informações do repasse</Text>
            <div className="drawer-summary">
              <div className="drawer-summary-row">
                <Text variant="body-sm">Valor do repasse</Text>
                <Text variant="label-sm">R$ {valorRepasse.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
              </div>
              <div className="drawer-summary-divider" />
              <div className="drawer-summary-row">
                <Text variant="body-sm">Liquidação centralizada (D+1)</Text>
                <Text variant="label-sm">R$ {(valorRepasse * 0.3).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
              </div>
              <div className="drawer-summary-divider" />
              <div className="drawer-summary-row">
                <Text variant="body-sm">Repasse Barte (D+0)</Text>
                <Text variant="label-sm">R$ {(valorRepasse * 0.7).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
              </div>
              <div className="drawer-summary-divider" />
              <div className="drawer-summary-row">
                <Text variant="body-sm">De</Text>
                <Text variant="label-sm">Saldo disponível</Text>
              </div>
              <div className="drawer-summary-divider" />
              <div className="drawer-summary-row">
                <Text variant="body-sm">Para</Text>
                <Text variant="label-sm">Conta Corrente</Text>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isFilled = saqueStep === 'filled';
    const { excede, taxa, recebe } = calcSaque(saqueValue);

    const fmt = (n: number) =>
      n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
      <div className="drawer-saque">
        <Text variant="h4">
          {isFilled ? 'Qual o valor da solicitação?' : 'Qual o valor do saque?'}
        </Text>

        {/* Breakdown */}
        <div className="drawer-breakdown">
          <div className="drawer-breakdown-bar">
            <div className="drawer-bar-segment" style={{ flex: 340, background: 'var(--accent-green)' }} />
            <div className="drawer-bar-segment" style={{ flex: 120, background: 'var(--accent-blue)' }} />
          </div>
          <div className={`drawer-breakdown-rows${isFilled ? ' drawer-breakdown-rows--dimmed' : ''}`}>
            <div className="drawer-breakdown-row">
              <div className="drawer-breakdown-label">
                <span className="drawer-dot" style={{ background: 'var(--accent-green)' }} />
                <Text variant="body-sm">Disponível</Text>
              </div>
              <Text variant="body-sm">R$ 340.000,00</Text>
            </div>
            <div className="drawer-breakdown-row">
              <div className="drawer-breakdown-label">
                <span className="drawer-dot" style={{ background: 'var(--accent-blue)' }} />
                <Text variant="body-sm">Disponível para antecipação</Text>
              </div>
              <Text variant="body-sm">R$ 120.000,00</Text>
            </div>
            <div className="drawer-breakdown-row">
              <Text variant="body-sm" style={{ fontWeight: 600 }}>Disponível total</Text>
              <Text variant="body-sm" style={{ fontWeight: 600 }}>R$ 460.000,00</Text>
            </div>
          </div>
        </div>

        {/* Input */}
        <Input
          placeholder="R$ 0,00"
          value={saqueValue ? `R$ ${saqueValue}` : ''}
          onChange={handleValueChange}
          error={excede ? 'Valor superior ao disponível para saque.' : undefined}
        />

        {/* Summary (estado preenchido e sem erro) */}
        {isFilled && !excede && (
          <div className="drawer-summary">
            <div className="drawer-summary-row">
              <Text variant="body-sm">Total solicitado</Text>
              <Text variant="body-sm">R$ {saqueValue}</Text>
            </div>
            <div className="drawer-summary-divider" />
            <div className="drawer-summary-row">
              <Text variant="body-sm">Taxa de antecipação</Text>
              <Text variant="body-sm">R$ {fmt(taxa)}</Text>
            </div>
            <div className="drawer-summary-divider" />
            <div className="drawer-summary-row">
              <Text variant="label-sm">Você recebe</Text>
              <Text variant="label-sm">R$ {fmt(recebe)}</Text>
            </div>
          </div>
        )}

        {/* Alert */}
        <Alert
          variant="info"
          title=""
          description="O valor será descontado do saldo disponível e aparecerá como em processamento até ser creditado na conta corrente, em até 24h."
          showCloseButton={false}
        />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <SellerLayout activeItem="v1">
      <main className="content">
          <div className="content-inner">
            <PageHeading title="Olá, Madu" />

            {/* ── Cards de saldo ─────────────────────────────────────── */}
            <div className="cards-row">
              <div className="card-with-action">
                <CardBalance>
                  <CardBalance.Info
                    variant="small"
                    title="Saldo total das vendas"
                    balance="500.000,00"
                    icon={Card1InfoIcon}
                    bottomSlot={
                      <div className="balance-info-bottom">
                        <div className="balance-info-bottom-label">
                          <Text variant="body-xs">Saldo em processamento</Text>
                          <TooltipAny
                            title="Saldo em processamento"
                            label="Valor de repasses já solicitados, que estão em processamento e ainda não foram creditados na conta corrente."
                            placement="top"
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'default' }}>
                              <Icon icon={InfoIcon} size={14} />
                            </span>
                          </TooltipAny>
                        </div>
                        <Text variant="body-xs">R$2.456,92</Text>
                      </div>
                    }
                  />
                </CardBalance>
                <div className="card-action-btn">
                  <Button variant="outline" size="sm" onClick={handleOpenDrawer}>Sacar</Button>
                </div>
              </div>

              <div className="card-double">
                <CardBalance>
                  <CardBalance.Info
                    variant="small"
                    title="Saldo disponível"
                    balance="350.000,00"
                    icon={Card2Line1InfoIcon}
                    showPills
                    pills={<Pills label="Saldo em análise" state="warning" variant="light" size="sm" />}
                    rightSlot={
                      <CardBalance.BreakdownBar
                        items={[
                          { label: 'Disponível', value: 'R$ 340.000,00', amount: 340000, color: 'var(--accent-green)' },
                          { label: 'Garantia operacional', value: 'R$ 10.000,00', amount: 10000, color: 'var(--stroke-secondary)' },
                        ]}
                      />
                    }
                  />
                  <CardBalance.Info
                    variant="small"
                    title="Saldo futuro"
                    balance="150.000,00"
                    icon={Card2Line2InfoIcon}
                    rightSlot={
                      <CardBalance.BreakdownBar
                        items={[
                          { label: 'A liberar', value: 'R$ 120.000,00', amount: 120000, color: 'var(--accent-blue)' },
                          { label: 'Gravames', value: 'R$ 30.000,00', amount: 30000, color: 'var(--accent-orange)' },
                        ]}
                      />
                    }
                  />
                </CardBalance>
              </div>

              <div className="card-single card-with-action">
                <CardBalance color="black">
                  <CardBalance.Info
                    variant="small"
                    title="Conta corrente"
                    balance="450.502,05"
                    showIcon={false}
                    showPills
                    pills={<Pills label="Rendendo 98% CDI" state="success" variant="light" size="sm" />}
                  />
                </CardBalance>
                <div className="card-action-btn">
                  <Button variant="outline" size="sm">Pagar contas</Button>
                </div>
              </div>
            </div>

            {/* ── Seção Recebíveis ───────────────────────────────────── */}
            <div className="receivables-section">
              <div className="receivables-columns">

                {/* ── Coluna esquerda: gráfico ─────────────────────── */}
                <div className="receivables-chart-col">
                  <div className="receivables-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <div className="period-buttons">
                      <Button
                        variant={activePeriod === 'week' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('week')}
                      >
                        Semana
                      </Button>
                      <Button
                        variant={activePeriod === 'month' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('month')}
                      >
                        Mês
                      </Button>
                      <Button
                        variant={activePeriod === 'jan' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('jan')}
                      >
                        janeiro/2026
                      </Button>
                    </div>
                  </div>

                  <div className="chart-container">
                    <div className="chart-bars">
                      {CHART_DATA.map((d) => {
                        const receivedPct = (d.received / MAX_CHART_VALUE) * 100;
                        const overduePct = (d.overdue / MAX_CHART_VALUE) * 100;
                        return (
                          <div key={d.label} className="chart-col">
                            <div className="bar-group">
                              <div
                                className="bar bar-received"
                                style={{ height: `${receivedPct}%` }}
                              />
                              <div
                                className="bar bar-overdue"
                                style={{ height: `${overduePct}%` }}
                              />
                            </div>
                            <Text
                              variant="caption-sm"
                              style={{
                                color: d.isToday
                                  ? 'var(--content-primary)'
                                  : 'var(--content-tertiary)',
                                textAlign: 'center',
                                fontWeight: d.isToday ? 600 : undefined,
                              }}
                            >
                              {d.isToday ? 'hoje' : d.label}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Legenda — abaixo do gráfico */}
                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-dot legend-received" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Recebidos
                      </Text>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot legend-overdue" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Em atraso
                      </Text>
                    </div>
                  </div>
                </div>

                {/* ── Coluna direita: lista de recebíveis ──────────── */}
                <div className="receivables-list-col">
                  <div className="receivables-list-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <Button variant="link" size="sm">ver todos</Button>
                  </div>
                  <div className="receivables-search-placeholder" />
                  <div className="receivables-list">
                    {RECEBIVEIS_MOCK.map((item) => (
                      <div key={item.id} className="receivable-item">
                        <Avatar variant="text" size="md" initials={item.initials} />
                        <div className="receivable-item-info">
                          <Text variant="label-sm">{item.name}</Text>
                          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
                            {item.dueLabel}
                          </Text>
                        </div>
                        <div className="receivable-item-right">
                          <Text variant="label-sm">{item.amount}</Text>
                          <Text
                            variant="caption-sm"
                            style={{
                              color:
                                item.statusState === 'error'
                                  ? 'var(--accent-red)'
                                  : 'var(--accent-green)',
                            }}
                          >
                            {item.status}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </main>
    </SellerLayout>

      {/* ── Drawer: Solicitar saque ───────────────────────────────────────── */}
      <Drawer
        isOpen={drawerOpen}
        title="Solicitar saque"
        onClose={handleCloseDrawer}
        footer={getDrawerFooter()}
      >
        {renderDrawerContent()}
      </Drawer>
    </>
  );
}
